/**
 * Only for CC1 Pro page, page location pages/products/cc1-pro.page.tsx
 */

import { useTranslations } from 'next-intl'
import { useMemo } from 'react'
import { useChinaDetector } from '../common/region-locale'
import { useMobileDetector } from './use-mobile-detector'
import { Theme } from '@common/pd-meta-components-types'

type Section1Type = {
  mediaUrl: string
  titleUrl: string
  subTitle: string
  videoUrl: string
  theme?: string // dark,light,gray
  maskColor?: string
  maskDegree?: number
}

type Section2Type = {
  mediaUrl: string
  theme?: string
  maskColor?: string
  maskDegree?: number
}

type Section3Type = {
  dataList: Array<{
    mediaUrl: string
    title: string
    desc?: string
  }>
  theme?: string
}

type Section4Type = {
  title: string
  desc: string
  mediaUrl: string
  theme?: string
  maskColor?: string
  maskDegree?: number
}

type Section5Type = {
  title: string
  desc: string
  tagGroup?: Array<{ title: string; desc: string }>
  mediaUrl: string
  theme?: string
}

type Section6Type = {
  title: string
  mediaUrl: string
  tagGroup: Array<{
    title: string
    desc: string
    mediaUrl: string
  }>
  theme?: string
}

type Section7Type = {
  title: string
  desc: string
  mediaUrl: string
  theme?: string
}

type Section8Type = {
  title: string
  desc: string
  mediaUrl: string
  theme?: string
  maskColor?: string
  maskDegree?: number
}

type Section9Type = {
  title: string
  desc: string
  mediaUrl: string
  theme?: string
}

type Section10Type = {
  title: string
  desc: string
  mediaUrl: string
  tagList: Array<{
    title: string
    mediaUrl: string
  }>
  theme?: string
}

type Section11Type = {
  title: string
  mediaGroup: Array<{
    tabTitle: string
    mediaUrl: string
    tagGroup: Array<{
      title: string
      desc: string
    }>
  }>
  theme?: string
}

type Section12Type = {
  title: string
  desc: string
  mediaUrl: string
  tagGroup?: Array<{ title: string; desc: string }>
  theme?: string
}

type Section13Type = {
  title: string
  desc: string
  mediaGroup: Array<{
    title: string
    mediaUrl: string
  }>
  theme?: Theme
}

type Section14Type = {
  title: string
  desc: string
  mediaUrl: string
  tagGroup: Array<{
    title: string
    desc: string
  }>
  theme?: string
}

type Section15Type = {
  theme?: string
  title: string
  imgUrl: string
  paramsList: Array<{
    label: string
    value: Array<string>
  }>
  btntext: string
  robot: string
}

export function useFlashBotArm() {
  const t = useTranslations()
  const inChina = useChinaDetector()
  const isMobileDevice = useMobileDetector()

  const lang = useMemo<string>(() => {
    return inChina ? 'cn' : 'en'
  }, [inChina])

  const prefix = 'https://cdn.pudutech.com/official-website/product/CC1Pro'
  const section1Data: Section1Type = {
    theme: 'dark',
    mediaUrl: isMobileDevice ? `${prefix}/S1-2H5.webp` : `${prefix}/S1-2.webp`,
    titleUrl: `${prefix}/S1-1.svg`,
    subTitle: t('Section1.desc'),
    videoUrl: inChina ? `${prefix}/S1-4.mp4` : `${prefix}/S1-4-en.mp4`,
  }

  const section2Data: Section2Type = {
    mediaUrl: `${prefix}/S2-1.mov`,
  }

  const section3Data: Section3Type = {
    theme: 'dark',
    dataList: [
      {
        mediaUrl: `${prefix}/S3-1.svg`,
        title: t('Section3.dataList.0.title'),
      },
      {
        mediaUrl: `${prefix}/S3-2.svg`,
        title: t('Section3.dataList.1.title'),
      },
      {
        mediaUrl: `${prefix}/S3-3.svg`,
        title: t('Section3.dataList.2.title'),
      },
      {
        mediaUrl: `${prefix}/S3-4.svg`,
        title: t('Section3.dataList.3.title'),
      },
      {
        mediaUrl: `${prefix}/S3-5.svg`,
        title: t('Section3.dataList.4.title'),
      },
      {
        mediaUrl: `${prefix}/S3-6.svg`,
        title: t('Section3.dataList.5.title'),
      },
      {
        mediaUrl: `${prefix}/S3-7.svg`,
        title: t('Section3.dataList.6.title'),
      },
      {
        mediaUrl: `${prefix}/S3-8.svg`,
        title: t('Section3.dataList.7.title'),
      },
    ],
  }

  const section4Data: Section4Type = {
    theme: 'dark',
    title: t('Section4.title'),
    desc: t('Section4.desc'),
    mediaUrl: isMobileDevice ? `${prefix}/S4-1-H5.webp` : `${prefix}/S4-1.webp`,
  }

  const section5Data: Section5Type = {
    theme: 'dark',
    title: t('Section5.title'),
    desc: t('Section5.desc'),
    mediaUrl: `${prefix}/S5-1.webp`,
    tagGroup: t.raw('Section5.tagGroup'),
  }

  const section6Data: Section6Type = {
    theme: 'dark',
    title: t('Section6.title'),
    mediaUrl: `${prefix}/S6-1.webp`,
    tagGroup: t.raw('Section6.tagGroup'),
  }

  const section7Data: Section7Type = {
    theme: 'dark',
    title: t('Section7.title'),
    desc: t('Section7.desc'),
    mediaUrl: `${prefix}/S7-1.webp`,
  }

  const section8Data: Section8Type = {
    theme: 'dark',
    title: t('Section8.title'),
    desc: t('Section8.desc'),
    mediaUrl: `${prefix}/S8-1.webp`,
  }

  const section9Data: Section9Type = {
    theme: 'dark',
    title: t('Section9.title'),
    desc: t('Section9.desc'),
    mediaUrl: `${prefix}/S9-1.webp`,
  }

  const section10Data: Section10Type = {
    theme: 'dark',
    title: t('Section10.title'),
    desc: t('Section10.desc'),
    mediaUrl: `${prefix}/S-10.mp4`,
    tagList: t.raw('Section10.tagGroup').map((item: { title: string }) => ({
      title: item.title,
      mediaUrl: 'https://cdn.pudutech.com/official-website/mt1/S5-1.webp',
    })),
  }

  const section11Data: Section11Type = {
    theme: 'dark',
    title: t('Section11.title'),
    mediaGroup: [
      {
        tabTitle: t('Section11.mediaGroup.0.tabTitle'),
        mediaUrl: inChina ? `${prefix}/S11-1-cn.webp` : `${prefix}/S11-1-en.webp`,
        tagGroup: t.raw('Section11.mediaGroup.0.tagGroup'),
      },
      {
        tabTitle: t('Section11.mediaGroup.1.tabTitle'),
        mediaUrl: inChina ? `${prefix}/S11-2-cn.webp` : `${prefix}/S11-2-en.webp`,
        tagGroup: t.raw('Section11.mediaGroup.1.tagGroup'),
      },
    ],
  }

  const section12Data: Section12Type = {
    theme: 'dark',
    title: t('Section12.title'),
    desc: t('Section12.desc'),
    mediaUrl: `${prefix}/S12-1.webp`,
    // tagGroup: t.raw('Section12.tagGroup').map((item: { title: string; desc: string }) => ({
    //   title: item.title,
    //   desc: item.desc,
    //   icon: 'https://cdn.pudutech.com/official-website/mt1/S5-1.webp',
    // })),
  }

  const section13Data: Section13Type = {
    theme: Theme.Dark,
    title: t('Section13.title'),
    desc: t('Section13.desc'),
    mediaGroup: [
      {
        title: t('Section13.features.0.title'),
        mediaUrl: `${prefix}/S13-1.webp`,
      },
      {
        title: t('Section13.features.1.title'),
        mediaUrl: `${prefix}/S13-2.webp`,
      },
      {
        title: t('Section13.features.2.title'),
        mediaUrl: inChina ? `${prefix}/S13-3-cn.webp` : `${prefix}/S13-3-en.webp`,
      },
    ],
  }

  const section14Data: Section14Type = {
    theme: 'dark',
    title: t('Section14.title'),
    desc: t('Section14.desc'),
    mediaUrl: `${prefix}/S14.webp`,
    tagGroup: t.raw('Section14.tagGroup'),
  }

  const section15Data: Section15Type = {
    theme: 'dark',
    title: t('Section15.title'),
    robot: 'cc1_pro',
    imgUrl: `${prefix}/S15.webp`,
    paramsList: t.raw('Section15.paramsList'),
    btntext: t('ContactUs'),
  }
  return {
    section1Data,
    section2Data,
    section3Data,
    section4Data,
    section5Data,
    section6Data,
    section7Data,
    section8Data,
    section9Data,
    section10Data,
    section11Data,
    section12Data,
    section13Data,
    section14Data,
    section15Data,
  }
}
